import { phSpinnerLight } from "@vintecc/components/icons";
import { Icon, tokens, Textarea, Button, Input } from "@vintecc/components";
import { useCallback, useState, useEffect } from "react";
import { IUserMfa } from "./types";
import ENV from "../../env";
import { useAuthTranslation } from "../../services/culture/auth.translations";

function buildValidatedUrl(apiServer: string, username: string, registerCode: string): string {
  try {
    // API_SERVER is deployment-controlled and may be relative (e.g. /api) or absolute.
    const url = new URL(apiServer, window.location.origin);
    if (!["http:", "https:"].includes(url.protocol) || url.username || url.password) {
      throw new Error("Invalid protocol");
    }

    const normalizedPath = url.pathname.replace(/\/+$/, "");
    url.pathname = `${normalizedPath}/mfa/enable`;
    url.search = "";

    url.searchParams.set("username", username);
    url.searchParams.set("registerCode", registerCode);
    return url.href;
  } catch {
    throw new Error("Invalid URL");
  }
}

const MfaContent = ({
  testId,
  username,
  registerCode,
  onChange,
}: {
  testId: string;
  username: string;
  registerCode: string;
  onChange: (mfa: IUserMfa) => void;
}) => {
  const { t: tAuth } = useAuthTranslation();
  const getMfa = useCallback(async () => {
    try {
      return await fetch(buildValidatedUrl(ENV.API_SERVER, username, registerCode)).then(async (response) => {
        if (response.status === 401) {
          return undefined;
        }
        return response.json();
      });
    } catch {
      return undefined;
    }
  }, [registerCode, username]);
  const [showImage, setShowImage] = useState<boolean>(true);
  const [mfa, setMfa] = useState<IUserMfa | undefined>(undefined);

  useEffect(() => {
    getMfa().then((data) => {
      setMfa(data);
    });
  }, [getMfa]);

  if (!mfa) {
    return (
      <div className="flex flex-col items-center" data-testid={testId}>
        <div className="relative h-56 w-56 flex justify-center items-center bg-white">
          <Icon icon={phSpinnerLight} spin size="2xl" color={tokens.theme.text.color} />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2" data-testid={testId}>
      <div className="flex flex-col gap-1 items-center">
        {showImage ? (
          <img src={mfa.barcodeImageUrl} alt={tAuth("mfa.qrCode")} />
        ) : (
          <div className="w-full">
            <Textarea label={tAuth("mfa.setupCode")} value={mfa.setupCode} readOnly rows={5} testId={`${testId}-setup-code`} />
          </div>
        )}

        <Button type="button" variant="plain" onClick={() => setShowImage(!showImage)} testId={`${testId}-toggle-button`}>
          {showImage ? tAuth("mfa.orUseSetupCode") : tAuth("mfa.orUseQrCode")}
        </Button>
      </div>

      <Input
        testId={`${testId}-pin-code-input`}
        type="text"
        label={tAuth("mfa.mfaCode")}
        required
        value={mfa.pinCode}
        pattern="^[0-9]{6}$"
        onChange={(e) => {
          const data = { ...mfa, pinCode: e.target.value };
          setMfa(data);
          onChange(data);
        }}
      />
    </div>
  );
};

export default MfaContent;
